import { all, call, delay, put, takeEvery, takeLatest } from 'redux-saga/effects';
import SuperFetch from 'library/helpers/superFetch';
import { Notification } from 'zComponents/atoms';
import { demoActions } from './slice';

/*
 * ⚠️ `*WithoutOrg` — /v5/demo is a GLOBAL staff mount, above /:orgID. Calling it through the normal
 * org-scoped helper would produce /v5/{someOrg}/demo/status, which does not exist.
 */

const TERMINAL = ['succeeded', 'failed', 'unknown'];
const POLL_MS = 3000;
/*
 * A full restore measured 50s on dev. 60 polls at 3s is a 3-minute ceiling — generous enough for a
 * cold start and a slower production dataset, and bounded so a job that never reports back stops
 * polling instead of hammering the API for the rest of the session.
 */
const MAX_POLLS = 60;

export function* loadDemoStatusSaga() {
	try {
		const res = yield call(() => SuperFetch.getWithoutOrg('/demo/status'));
		if (res.statusCode === 200 || res.statusCode === 201) {
			yield put(demoActions.loadDemoStatusSuccess(res.data));
		} else {
			yield put(demoActions.loadDemoStatusError(res.error || 'Could not load demo status'));
		}
	} catch (error) {
		yield put(demoActions.loadDemoStatusError(error.message));
	}
}

export function* restoreDemoOrgSaga({ payload }) {
	const { orgID } = payload;
	try {
		const res = yield call(() => SuperFetch.postWithoutOrg(`/demo/${orgID}/restore`, {}));

		// 202 is the success case here — the work has been ACCEPTED, not finished.
		if (res.statusCode !== 202 && res.statusCode !== 200) {
			const message = res.error || res.info || 'Restore could not be started';
			Notification('error', 'Restore not started', message);
			yield put(demoActions.restoreDemoOrgSettled({ orgID, error: message }));
			return;
		}

		const job = res.data;
		yield put(demoActions.restoreDemoOrgAccepted(job));
		Notification('info', 'Restore started', 'This takes about a minute. You can leave this page.');

		let latest = job;
		for (let i = 0; i < MAX_POLLS && !TERMINAL.includes(latest?.state); i++) {
			yield delay(POLL_MS);
			const poll = yield call(() => SuperFetch.getWithoutOrg(`/demo/restore/${job.id}`));
			if (poll.statusCode === 200) latest = poll.data;
		}

		if (latest?.state === 'succeeded') {
			Notification('success', 'Demo restored', `Workspace ${orgID} was purged and re-seeded.`);
		} else if (latest?.state === 'failed') {
			Notification('error', 'Restore failed', latest.error || 'See the server log for detail.');
		} else {
			/*
			 * Neither succeeded nor failed: the poll ceiling was hit, or the instance was recycled
			 * mid-job and the server aged the job out to `unknown`. Say that, rather than reporting a
			 * success we did not observe — the workspace may be half-restored.
			 */
			Notification(
				'warning',
				'Restore state unknown',
				'The job stopped reporting. Refresh the status below before starting another.'
			);
		}

		yield put(demoActions.restoreDemoOrgSettled({ orgID, job: latest }));
		yield put(demoActions.loadDemoStatus());
	} catch (error) {
		Notification('error', 'Restore failed', error.message);
		yield put(demoActions.restoreDemoOrgSettled({ orgID, error: error.message }));
	}
}

export default function* demoSaga() {
	yield all([
		takeLatest(demoActions.loadDemoStatus, loadDemoStatusSaga),
		// takeEvery, not takeLatest: the two workspaces restore independently and cancelling one
		// because the other started would abandon a job that is still running on the server.
		takeEvery(demoActions.restoreDemoOrg, restoreDemoOrgSaga)
	]);
}
